import type { DiffBackgroundTokens, Theme, ThemeName } from './tokens';

// The dark themes keep ANSI color names so they inherit the palette the user
// configured in their terminal. The light themes use concrete hex values:
// default ANSI colors (bright yellow, blue, cyan) are frequently unreadable on
// light backgrounds. Those hex values all live in the `color` object below and
// are referenced as `color.<ramp>[<step>]`: `light` uses steps that clear WCAG
// AA (4.5:1) and `light-high-contrast` uses steps that clear AAA (7:1) on a
// white background. Hex colors degrade gracefully via chalk on terminals
// without truecolor support.

/**
 * GitLab Design System constant color tokens used by the built-in themes —
 * subset of https://design.gitlab.com/product-foundations/color, named
 * `color.<ramp>[<step>]` to match the token directory.
 */
const color = {
  neutral: {
    50: '#ececef',
    100: '#dcdcde',
    500: '#737278',
    800: '#3a383f',
    900: '#28272d',
    950: '#18171d',
  },
  green: {
    100: '#c3e6cd',
    200: '#91d4a8',
    600: '#2f7549',
    800: '#225131',
    950: '#17291c',
  },
  red: {
    100: '#fdd4cd',
    200: '#fcb5aa',
    600: '#c02f12',
    800: '#812713',
    900: '#582014',
    950: '#3e1a14',
  },
  orange: {
    700: '#894b16',
    800: '#693c14',
  },
  blue: {
    100: '#cbe2f9',
    200: '#9dc7f1',
    600: '#2f68b4',
    700: '#2f5ca0',
    800: '#284779',
    900: '#213454',
    950: '#1d283e',
  },
  purple: {
    700: '#5c47a6',
    800: '#493c83',
  },
} as const;

const DARK_DIFF: DiffBackgroundTokens = {
  addPrefix: color.green[800],
  add: color.green[950],
  delPrefix: color.red[900],
  del: color.red[950],
  plainPrefix: color.neutral[900],
  plain: color.neutral[950],
  hunkPrefix: color.blue[900],
  hunk: color.blue[950],
};

const LIGHT_DIFF: DiffBackgroundTokens = {
  addPrefix: color.green[200],
  add: color.green[100],
  delPrefix: color.red[200],
  del: color.red[100],
  plainPrefix: color.neutral[100],
  plain: color.neutral[50],
  hunkPrefix: color.blue[200],
  hunk: color.blue[100],
};

export const darkTheme: Theme = {
  name: 'dark',
  appearance: 'dark',
  tokens: {
    text: {
      default: 'white',
      subtle: 'gray',
      inverse: 'black',
      success: 'green',
      danger: 'red',
      warning: 'yellow',
      info: 'cyan',
      accent: 'cyan',
      link: 'blue',
      code: 'yellow',
      agent: {
        build: 'cyan',
        plan: 'magenta',
      },
    },
    background: {
      userMessage: color.neutral[900],
      diff: DARK_DIFF,
    },
  },
};

// TODO: right now this theme is not fully system like darkTheme is, as it uses colors from gitlab pajamas styleguide and not the terminal colors.
// we should change it in the scope of https://gitlab.com/gitlab-org/editor-extensions/gitlab-lsp/-/work_items/2887
// creating an light theme based on system colors, and rename this one to something like gitlab-light
export const lightTheme: Theme = {
  name: 'light',
  appearance: 'light',
  tokens: {
    text: {
      default: 'black',
      subtle: color.neutral[500],
      inverse: 'white',
      success: color.green[600],
      danger: color.red[600],
      warning: color.orange[700],
      info: color.blue[600],
      accent: color.blue[600],
      link: color.blue[700],
      code: color.orange[700],
      agent: {
        // One step darker than `info`: these render on background.userMessage
        // (color.neutral.100), where the .600 steps fall below AA.
        build: color.blue[700],
        plan: color.purple[700],
      },
    },
    background: {
      userMessage: color.neutral[100],
      diff: LIGHT_DIFF,
    },
  },
};

export const darkHighContrastTheme: Theme = {
  name: 'dark-high-contrast',
  appearance: 'dark',
  tokens: {
    text: {
      default: 'whiteBright',
      subtle: 'white',
      inverse: 'black',
      success: 'greenBright',
      danger: 'redBright',
      warning: 'yellowBright',
      info: 'cyanBright',
      accent: 'cyanBright',
      link: 'blueBright',
      code: 'yellowBright',
      agent: {
        build: 'cyanBright',
        plan: 'magentaBright',
      },
    },
    background: {
      userMessage: color.neutral[900],
      diff: DARK_DIFF,
    },
  },
};

export const lightHighContrastTheme: Theme = {
  name: 'light-high-contrast',
  appearance: 'light',
  tokens: {
    text: {
      default: 'black',
      subtle: color.neutral[800],
      inverse: 'white',
      success: color.green[800],
      danger: color.red[800],
      warning: color.orange[800],
      info: color.blue[800],
      accent: color.blue[800],
      link: color.blue[900],
      code: color.orange[800],
      agent: {
        build: color.blue[800],
        plan: color.purple[800],
      },
    },
    background: {
      userMessage: color.neutral[100],
      diff: LIGHT_DIFF,
    },
  },
};

export const THEMES: Record<ThemeName, Theme> = {
  dark: darkTheme,
  light: lightTheme,
  'dark-high-contrast': darkHighContrastTheme,
  'light-high-contrast': lightHighContrastTheme,
};
